/**
 * Lift2Fit — the app router primitives.
 *
 * A tiny history-API router: no dependency, no hash-only URLs in normal use.
 * A `#/route` deep link (used by the static preview server and by the browser
 * check) is adopted on first read and normalised to a clean path, so the same
 * links work whether the app is served by vite preview or a real API host.
 */
import { useEffect, useState, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react';

export function currentPath(): string {
  if (typeof window === 'undefined') return '/';
  const { hash, pathname } = window.location;
  if (hash.startsWith('#/')) return hash.slice(1);
  return pathname || '/';
}

function normalise(path: string): string {
  // A route may carry a query (`/programme/all?clientId=…`): normalise the path
  // alone and KEEP the query, so a reload or a deep link keeps its context. The
  // matcher strips it again before comparing route names.
  const [pathPart, query] = path.split('?');
  const clean = pathPart?.replace(/\/+$/, '') ?? '';
  const base = clean === '' ? '/' : clean;
  return query === undefined ? base : `${base}?${query}`;
}

export function navigate(to: string, options: { replace?: boolean } = {}): void {
  if (typeof window === 'undefined') return;
  const target = normalise(to);
  if (target === normalise(currentPath())) return;
  window.history[options.replace ? 'replaceState' : 'pushState']({}, '', target);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** Subscribe to history changes and return the current path. */
export function useRoute(): string {
  const [path, setPath] = useState(currentPath);

  useEffect(() => {
    const sync = () => setPath(currentPath());
    // adopt an initial `#/x` deep link once, then keep the URL clean
    if (window.location.hash.startsWith('#/')) {
      window.history.replaceState({}, '', normalise(currentPath()));
    }
    sync();
    window.addEventListener('popstate', sync);
    window.addEventListener('hashchange', sync);
    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener('hashchange', sync);
    };
  }, []);

  return path;
}

export interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  to: string;
  children: ReactNode;
}

/**
 * `href` keeps the hash form so middle-click, open-in-new-tab and no-JS hosts
 * still land on the right route; a plain left click stays client-side.
 */
export function Link({ to, children, onClick, ...rest }: LinkProps) {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(to);
  };
  return (
    <a href={`#${normalise(to)}`} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}

export interface RouteMatch {
  name: string;
  param?: string;
  /** A second path segment, e.g. the session inside `/crm/clients/:clientId/sessions/:sessionId`. */
  param2?: string;
}

/** `/` → landing (AppRouter renders nothing so the marketing page stays). */
export function matchRoute(path: string): RouteMatch {
  // The query carries page context (`/programme/all?clientId=…`), never identity
  // for the matcher: strip it so route names compare exactly as before.
  const clean = normalise(path).split('?')[0] ?? '';
  if (clean === '/') return { name: 'landing' };
  if (clean === '/login') return { name: 'login' };
  if (clean === '/signup') return { name: 'signup' };
  // The public legal documents. Readable signed out, because consent happens
  // before an account exists; the signup checkbox links straight to them.
  if (clean === '/legal') return { name: 'legal-index' };
  const legal = clean.match(/^\/legal\/([^/]+)$/);
  if (legal?.[1]) return { name: 'legal', param: decodeURIComponent(legal[1]) };
  if (clean === '/dashboard') return { name: 'dashboard' };
  if (clean === '/grid') return { name: 'grid' };
  // One completed session, opened from the Grid's own history. Deep-linkable so
  // a lifter can share or reload it; the back action returns to /grid.
  const gridSession = clean.match(/^\/grid\/sessions\/([^/]+)$/);
  if (gridSession?.[1]) return { name: 'grid-session', param: decodeURIComponent(gridSession[1]) };
  if (clean === '/programme') return { name: 'programme' };
  // Every block, with the detail the inline list no longer carries (C). The
  // coach reuses the same page read-only with `?clientId=`.
  if (clean === '/programme/all') return { name: 'programme-all' };
  if (clean === '/summary') return { name: 'summary' };
  if (clean === '/goals') return { name: 'goals' };
  // The lifter's own session schedule: the "when" layer over the undated
  // programme. A lifter route in the lifter shell.
  if (clean === '/schedule') return { name: 'schedule' };
  // The lifter's own coaching surface. Deliberately NOT under `/coach`, which
  // is the coach's authoring desk and selects the coach shell.
  if (clean === '/my-coach') return { name: 'my-coach' };
  // The reminder's "book a slot" path lands straight on the Booking tab rather
  // than the Coach tab, so the action goes where it says it goes.
  if (clean === '/my-coach/booking') return { name: 'my-coach-booking' };
  if (clean === '/coach') return { name: 'coach' };
  if (clean === '/coach/join') return { name: 'coach-join' };
  // The standalone chat workspace (0028 IA): its own small app, outside the CRM
  // shell. Opened in a new window and deep-linkable (`#/chat?clientId=…`).
  if (clean === '/chat') return { name: 'chat' };
  if (clean === '/plan/new') return { name: 'plan-new' };
  if (clean === '/profile') return { name: 'profile' };
  // Help: the account's one support thread. A lifter-shell route so
  // every account that is not a coach still has a door to the same conversation
  // the chat workspace pins for coaches.
  if (clean === '/help') return { name: 'help' };
  // The coaching workspace (CRM). One entity, sized by client count.
  // `/crm/signup` is the CRM's public front door: a coaching business creates
  // its account here and lands straight in the workspace. `/crm/login` is its
  // sibling — the business login. It is a real route rather than a query on
  // `/login` because this router normalises query strings away for matching and
  // for every link it writes, so `?audience=crm` would be silently dropped.
  if (clean === '/crm/signup') return { name: 'crm-signup' };
  if (clean === '/crm/login') return { name: 'crm-login' };
  if (clean === '/crm') return { name: 'crm-summary' };
  if (clean === '/crm/calendar') return { name: 'crm-calendar' };
  if (clean === '/crm/clients') return { name: 'crm-clients' };
  // The one-stop chat and the prospect funnel (0026): chatters on the left,
  // thread on the right; prospects kept off the paid-client roster.
  if (clean === '/crm/chat') return { name: 'crm-chat' };
  if (clean === '/crm/team') return { name: 'crm-team' };
  // The internal-coach invitation link (0033): `<base>#/crm/join-team?code=…`.
  if (clean === '/crm/join-team') return { name: 'crm-join-team' };
  if (clean === '/crm/prospects') return { name: 'crm-prospects' };
  if (clean === '/crm/packs') return { name: 'crm-packs' };
  // The coach's own business profile and settings. Deliberately in the coach
  // shell: it is the BUSINESS's page, never the lifter profile at `/profile`.
  if (clean === '/crm/profile') return { name: 'crm-profile' };
  if (clean === '/crm/todo') return { name: 'crm-todo' };
  if (clean === '/crm/payments') return { name: 'crm-payments' };
  if (clean === '/crm/insights') return { name: 'crm-insights' };
  const crmClientSession = clean.match(/^\/crm\/clients\/([^/]+)\/sessions\/([^/]+)$/);
  if (crmClientSession?.[1] && crmClientSession[2]) {
    return {
      name: 'crm-client-session',
      param: decodeURIComponent(crmClientSession[1]),
      param2: decodeURIComponent(crmClientSession[2]),
    };
  }
  const crmClientBlock = clean.match(/^\/crm\/clients\/([^/]+)\/blocks\/([^/]+)$/);
  if (crmClientBlock?.[1] && crmClientBlock[2]) {
    return {
      name: 'crm-client-block',
      param: decodeURIComponent(crmClientBlock[1]),
      param2: decodeURIComponent(crmClientBlock[2]),
    };
  }
  const crmClientProgrammes = clean.match(/^\/crm\/clients\/([^/]+)\/programmes$/);
  if (crmClientProgrammes?.[1]) {
    return { name: 'crm-client-programmes', param: decodeURIComponent(crmClientProgrammes[1]) };
  }
  const crmClient = clean.match(/^\/crm\/clients\/([^/]+)$/);
  if (crmClient?.[1]) return { name: 'crm-client', param: decodeURIComponent(crmClient[1]) };
  // Phase 1's `/clients` is folded into the workspace: the old link redirects
  // so there is one client surface, never two.
  if (clean === '/clients') return { name: 'legacy-clients' };
  const legacyClient = clean.match(/^\/clients\/([^/]+)$/);
  if (legacyClient?.[1]) return { name: 'legacy-client', param: decodeURIComponent(legacyClient[1]) };
  // The founder's admin surface. Invisible to every account that does not hold
  // the admin role; the route guard refuses a direct deep link with the
  // in-app "Admin access" panel.
  if (clean === '/admin') return { name: 'admin-overview' };
  if (clean === '/admin/lifters') return { name: 'admin-lifters' };
  if (clean === '/admin/accounts') return { name: 'admin-accounts' };
  if (clean === '/admin/businesses') return { name: 'admin-businesses' };
  if (clean === '/admin/revenue') return { name: 'admin-revenue' };
  if (clean === '/admin/plans') return { name: 'admin-plans' };
  if (clean === '/admin/offers') return { name: 'admin-offers' };
  if (clean === '/admin/invitations') return { name: 'admin-invitations' };
  if (clean === '/admin/system') return { name: 'admin-system' };
  if (clean === '/admin/audit') return { name: 'admin-audit' };
  // The founder's own account page.
  // `/admin/profile` exists because the account menu used to send an admin to
  // the lifter profile, which is a different product's page.
  if (clean === '/admin/profile') return { name: 'admin-profile' };
  // 0038 — the support team (staff) and its desk.
  if (clean === '/admin/staff') return { name: 'admin-staff' };
  if (clean === '/staff') return { name: 'staff' };
  // The platform's chat interface is its OWN workspace, rendered without the
  // admin shell — the admin's twin of `#/chat`. `/admin/support` was the first
  // name for it and still resolves here, so an old link does not 404.
  if (clean === '/admin/chat' || clean === '/admin/support') return { name: 'admin-chat' };
  const adminAccount = clean.match(/^\/admin\/accounts\/([^/]+)$/);
  if (adminAccount?.[1]) return { name: 'admin-account', param: decodeURIComponent(adminAccount[1]) };
  // The public invitation accept link the admin hands out. Outside the app
  // shell and outside the admin gate: the invitee may have no account yet.
  const join = clean.match(/^\/join\/([^/]+)$/);
  if (join?.[1]) return { name: 'join', param: decodeURIComponent(join[1]) };
  const plan = clean.match(/^\/plan\/([^/]+)$/);
  if (plan?.[1]) return { name: 'plan', param: decodeURIComponent(plan[1]) };
  return { name: 'not-found' };
}
